import { Component, OnInit, OnDestroy, inject } from "@angular/core";
import {
  Validators,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  FormsModule
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

import { SignUpAvatarService } from "../services/avatar.service";
import { AvatarUploadModalComponent } from "../../shared/components/imageUploader/uploadModal/avatar-upload-modal.component";

import { SignUpNavigationService, Step } from "../services/navigation.service";
import { SignUpCreateAccountService } from "../services/create-account.service";
import { SignUpUserCustomizationService } from "../services/user-customization.service";
import { SignUpEntryService } from "../services/entry.service";
import { SignUpRedirectModalService } from "../services/redirect.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  matchingPasswordValidator,
  atLeastOneNumberValidator,
  atLeastOneSymbolValidator,
  invalidCharacterValidator
} from "../../shared/components/password/password.validator";
import { SignUpShotCallerService } from "../services/shot-caller.service";
import { SignupAnalyticsService } from "../services/analytics.service";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { Config } from "@onsip/common/config";
import { SignUpCommonLayoutComponent } from "../signupCommonLayout/signup-common-layout.component";
import { AvatarComponent } from "../../shared/components/avatar/avatar.component";
import { TitleCasePipe } from "@angular/common";
import { MatError, MatFormField, MatHint, MatLabel } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";

const debug = true;
@Component({
  selector: "onsip-signup-profile",
  templateUrl: "./signup-profile.component.html",
  styleUrls: ["./signup-profile.component.scss"],
  imports: [
    SignUpCommonLayoutComponent,
    AvatarComponent,
    TranslateModule,
    ReactiveFormsModule,
    TitleCasePipe,
    MatFormField,
    MatLabel,
    MatError,
    MatHint,
    MatInputModule,
    MatButtonModule,
    FormsModule
  ]
})
export class SignUpProfileComponent implements OnInit, OnDestroy {
  entryService = inject(SignUpEntryService);
  private avatarService = inject(SignUpAvatarService);
  private dialog = inject(MatDialog);
  private navigate = inject(SignUpNavigationService);
  private account = inject(SignUpCreateAccountService);
  private userCustomization = inject(SignUpUserCustomizationService);
  private translate = inject(TranslateService);
  private shotCaller = inject(SignUpShotCallerService);
  private analytics = inject(SignupAnalyticsService);
  private redirectModal = inject(SignUpRedirectModalService);
  private snackbar = inject(SnackbarService);
  private fb = inject(NonNullableFormBuilder);

  isBeta = Config.ADMIN_API_URL.includes("beta");
  avatarPlaceholder = "resources/img/signup/profile-placeholder.png";
  tempAvatar!: string;

  profileInfo = this.fb.group(
    {
      fullname: [
        "",
        [
          // regex: Letters, spaces, periods, quotes, and dashes only
          Validators.pattern("^[A-Za-z\\s'\\-\\.]+$"),
          Validators.maxLength(60),
          Validators.required
        ]
      ],
      username: [
        "",
        [Validators.pattern("^[A-Za-z\\s'\\-]+$"), Validators.maxLength(60), Validators.required]
      ],
      pword: [""],
      verify: [""],
      title: [this.userCustomization.title],
      orgName: [""]
    },
    {
      validators: matchingPasswordValidator("pword", "verify")
    }
  );

  private unsubscriber = new Subscription();

  ngOnInit() {
    debug && console.log("Signup2.ProfileComponent OnInit");

    // update the create account and userCustomization services everytime the form updates
    // so that preview fields stay up to date
    this.unsubscriber.add(
      this.profileInfo.valueChanges.subscribe(() => {
        if (this.profileInfo.controls.fullname.valid) {
          this.account.name = this.profileInfo.controls.fullname.value;
        }
        if (this.profileInfo.controls.username.valid) {
          this.account.username = (this.profileInfo.value.username as string).toLowerCase();
        }
        if (this.profileInfo.controls.pword.valid && this.profileInfo.controls.verify.valid) {
          this.account.password = this.profileInfo.controls.pword.value;
        }
        if (this.profileInfo.controls.title.valid) {
          this.userCustomization.title = this.profileInfo.controls.title.value;
        }
        if (this.isBeta && this.profileInfo.controls.orgName.valid) {
          this.account.orgName = this.profileInfo.controls.orgName.value;
        }
      })
    );

    // get values from entry service and set on the form
    this.profileInfo.controls.fullname.setValue(this.entryService.name || "");
    this.profileInfo.controls.username.setValue(this.entryService.username || "");
    this.profileInfo.controls.orgName.setValue(this.entryService.orgName || "");

    // only set validators on password fields if they are going to be displayed
    if (!this.entryService.isOauth) {
      this.profileInfo.controls.pword.setValidators([
        Validators.required,
        Validators.minLength(12),
        Validators.maxLength(64),
        // regex: any normal chars but cannot start or end with whitespace
        Validators.pattern("^[\\S][\\S\\s]*[\\S]+$"),
        atLeastOneNumberValidator(),
        atLeastOneSymbolValidator(),
        invalidCharacterValidator()
      ]);
      this.profileInfo.controls.verify.setValidators(Validators.required);
    }

    if (this.isBeta) {
      this.profileInfo.controls.orgName.setValidators([Validators.required]);
    }

    // HACK to get password error updates to work properly
    this.unsubscriber.add(
      this.profileInfo.statusChanges.subscribe(() => {
        const pwordControl = this.profileInfo.controls.pword;
        const pwordErrors = pwordControl.errors;
        const verifyControl = this.profileInfo.controls.verify;
        const verifyErrors = verifyControl.errors;
        if (this.profileInfo.hasError("notMatchingPassword") && verifyControl.touched) {
          // need to keep existing errors regardless if they are not matching
          pwordControl.setErrors(
            { ...pwordErrors, notMatchingPassword: true },
            { emitEvent: false }
          );
          verifyControl.setErrors(
            { ...verifyErrors, notMatchingPassword: true },
            { emitEvent: false }
          );
        } else {
          // only remove not matching errors but keep existing errors
          if (pwordErrors) delete pwordErrors.notMatchingPassword;
          if (verifyErrors) delete verifyErrors.notMatchingPassword;
          this.profileInfo.controls.pword.setErrors(pwordErrors, { emitEvent: false });
          this.profileInfo.controls.pword.updateValueAndValidity({ emitEvent: false });
          this.profileInfo.controls.verify.setErrors(verifyErrors, { emitEvent: false });
          this.profileInfo.controls.verify.updateValueAndValidity({ emitEvent: false });
        }
      })
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  uploadAvatar(): void {
    debug && console.log("Signup2.ProfileComponent uploadAvatar");
    this.analytics.touched = true;
    const dialogRef = this.dialog.open(AvatarUploadModalComponent, {
      panelClass: ["onsip-dialog-universal-style"],
      data: { useTempAvatar: true, currentAvatar: this.avatarService.avatar }
    });

    this.unsubscriber.add(
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(response => {
          if (response.doSave) {
            this.tempAvatar = response.image;
            this.avatarService.setAvatar(this.tempAvatar);
          }
        })
    );
  }

  next(): void {
    debug && console.log("Signup2.ProfileComponent next");
    this.profileInfo.updateValueAndValidity();

    if (this.profileInfo.valid) {
      this.redirectModal.loadingModal("createAccount");
      this.shotCaller
        .signupAndLogin()
        .then(() => this.navigate.navigateForward(Step.SIGNUP_PROFILE))
        .catch(err => {
          this.redirectModal.closeDialog();
          this.snackbar.openSnackBar("Error creating your account", "error"); // TODO i18n
          throw new Error("Signup2: Something went wrong, unable to create account." + err);
        });
    }
  }

  getSubmitText(): string {
    return this.translate.instant("ONSIP_I18N.FINISH");
  }

  getNameErrors(type: "username" | "fullname"): string {
    const control = this.profileInfo.controls[type];
    const value =
      type === "username"
        ? this.translate.instant("ONSIP_I18N.USERNAME")
        : this.translate.instant("ONSIP_I18N.FULL_NAME");
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("maxlength")
        ? this.translate.instant("ONSIP_I18N._VALUE__IS_LONGER_THAN_60_CHARACTERS", { value })
        : control.hasError("pattern")
          ? this.translate.instant(
              "ONSIP_I18N._VALUE__CAN_ONLY_CONTAIN_LETTERS_SPACES_QUOTES_OR_DASHES",
              { value }
            )
          : "";
  }

  getOrgErrors() {
    const control = this.profileInfo.controls.orgName;
    if (control.hasError("required")) {
      return "Contact organization is required";
    }
    return "";
  }

  getPasswordErrors(controlName: "pword" | "verify"): string {
    const control = this.profileInfo.controls[controlName];
    const minLengthRequired: number =
      (control.errors && control.errors.minlength && control.errors.minlength.requiredLength) || 5;
    const value = this.translate.instant("ONSIP_I18N.PASSWORD");

    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("minlength")
        ? this.translate.instant("ONSIP_I18N.PASSWORD_MUST_BE_AT_LEAST__VALUE__CHARACTERS", {
            value: minLengthRequired
          })
        : control.hasError("maxlength")
          ? this.translate.instant("ONSIP_I18N.CHARACTER_LIMIT_REACHED")
          : control.hasError("atLeastOneNumber")
            ? this.translate.instant("ONSIP_I18N.PASSWORD_MUST_CONTAIN_AT_LEAST_ONE_NUMBER")
            : control.hasError("atLeastOneSymbol")
              ? this.translate.instant("ONSIP_I18N.PASSWORD_MUST_CONTAIN_AT_LEAST_ONE_SYMBOL")
              : control.hasError("invalidCharacter")
                ? this.translate.instant("ONSIP_I18N.PASSWORD_CONTAINS_AN_INVALID_CHARACTER")
                : control.hasError("pattern")
                  ? this.translate.instant(
                      "ONSIP_I18N.PASSWORD_CANNOT_CONTAIN_SPACES_AT_THE_BEGINNING_OR_END"
                    )
                  : control.parent &&
                      control.parent.hasError("notMatchingPassword") &&
                      controlName === "pword"
                    ? this.translate.instant("ONSIP_I18N.PASSWORDS_MUST_MATCH")
                    : "";
  }

  getTitleErrors(): string {
    return this.profileInfo.controls.title.invalid
      ? this.translate.instant("ONSIP_I18N.INVALID_JOB_TITLE")
      : "";
  }
}
